import { useCallback, useEffect, useRef, useState } from 'react'

import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import { useEntitlement } from '@webapp/core/hooks/useEntitlement'
import {
  makeSelectContentTypesByModel,
  makeSelectFeatureSwitches,
  makeSelectUser,
} from '@webapp/core/stores/global'
import { useAppRoutes } from '@webapp/hooks'
import { useApproveExpensePresenter } from '@webapp/modules/expense/features/approve-expense/presenters/ApproveExpensePresenter'
import { useDeleteExpenseReportPresenter } from '@webapp/modules/expense/features/delete-expense-report/presenters/DeleteExpenseReportPresenter'
import { useDenyExpensePresenter } from '@webapp/modules/expense/features/deny-expense/presenters/DenyExpensePresenter'
import { useDuplicateExpenseReportPresenter } from '@webapp/modules/expense/features/duplicate-expense-report/presenters/DuplicateExpenseReportPresenter'
import { useGenerateExpensePdfPresenter } from '@webapp/modules/expense/features/generate-expense-pdf/presenters/GenerateExpensePdfPresenter'
import { useExpensePresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpensePresenter'
import { useSpendTrackerPresenter } from '@webapp/modules/expense/features/show-budget-info/presenters/SpendTrackerPresenter'
import {
  ExpenseApprovalsPageTabStatuses,
  ExpenseStatusPageTabStatuses,
} from '@webapp/modules/expense/pages/types'
import { expenseReportQueryConfig } from '@webapp/modules/expense/query-configs'
import { fetchCounts } from '@webapp/stores'

interface UseExpenseViewParams {
  uuid: string
  onPermissionsLoaded?: (data: { canApprove: boolean }) => void
}

export const useExpenseView = ({
  uuid,
  onPermissionsLoaded,
}: UseExpenseViewParams) => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const entitlement = useEntitlement()
  const featureSwitches = useSelector(makeSelectFeatureSwitches())
  const currentUser = useSelector(makeSelectUser())
  const { url } = useAppRoutes()

  const budgetsEnabled = entitlement.module.budgets.enabled

  const { expense, isLoading } = useExpensePresenter({ uuid })

  const {
    states: { hasBudgetData },
  } = useSpendTrackerPresenter({ expenseUuid: uuid })

  const [selectedNextApproverId, setSelectedNextApproverId] =
    useState<Nullable<number>>(null)
  const [isSpendTrackerOpen, setIsSpendTrackerOpen] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState('detail')

  const hasInitializedNextApprover = useRef(false)
  const hasNotifiedPermissions = useRef(false)
  const onPermissionsLoadedRef = useRef(onPermissionsLoaded)
  onPermissionsLoadedRef.current = onPermissionsLoaded

  useEffect(() => {
    if (!hasInitializedNextApprover.current && expense) {
      if (expense.hasSingleApproverChoice && expense.permissions.canApprove) {
        setSelectedNextApproverId(expense.nextApproverChoices[0]?.id ?? null)
      }
      hasInitializedNextApprover.current = true
    }
  }, [expense])

  useEffect(() => {
    window.analytics?.track('Expense Viewed')
  }, [])

  useEffect(() => {
    if (hasBudgetData) {
      setIsSpendTrackerOpen(true)
    }
  }, [hasBudgetData])

  useEffect(() => {
    if (!hasNotifiedPermissions.current && expense) {
      onPermissionsLoadedRef.current?.({
        canApprove: expense.permissions.canApprove,
      })
      hasNotifiedPermissions.current = true
    }
  }, [expense])

  const invalidateDetailQuery = useCallback(() => {
    queryClient.invalidateQueries(
      expenseReportQueryConfig.detail(uuid).queryKey
    )
  }, [queryClient, uuid])

  const handleApproveSuccess = useCallback(() => {
    invalidateDetailQuery()
    dispatch(fetchCounts())

    history.push(
      url('ExpenseApprovalsPage', {
        status: ExpenseApprovalsPageTabStatuses.PENDING,
      })
    )
  }, [invalidateDetailQuery, dispatch, history, url])

  const handleDenySuccess = useCallback(() => {
    invalidateDetailQuery()
    dispatch(fetchCounts())

    history.push(
      url('ExpenseApprovalsPage', {
        status: ExpenseApprovalsPageTabStatuses.DENIED,
      })
    )
  }, [invalidateDetailQuery, dispatch, history, url])

  const handleDeleteSuccess = useCallback(() => {
    history.push(
      url('ExpenseStatusPage', { status: ExpenseStatusPageTabStatuses.PENDING })
    )
  }, [history, url])

  const handleDuplicateSuccess = useCallback(() => {
    history.push(url('ExpenseRequestPage'))
  }, [history, url])

  const {
    commands: {
      approve: _approve,
      confirmOverBudget: _confirmOverBudget,
      cancelOverBudget,
    },
    states: { isApproving, isOverBudgetConfirmOpen },
  } = useApproveExpensePresenter({
    expenseId: expense?.id ?? 0,
    selectedNextApproverId,
    onSuccess: handleApproveSuccess,
  })

  const {
    commands: { deny: _deny },
    states: { isDenying },
  } = useDenyExpensePresenter({
    expenseId: expense?.id ?? 0,
    onSuccess: handleDenySuccess,
  })

  const {
    commands: {
      delete: _delete,
      openConfirm: openDeleteConfirm,
      closeConfirm: closeDeleteConfirm,
    },
    states: { isDeleting, isConfirmOpen: isDeleteConfirmOpen },
  } = useDeleteExpenseReportPresenter({
    expenseId: expense?.id ?? 0,
    onSuccess: handleDeleteSuccess,
  })

  const {
    commands: { duplicate: _duplicate },
    states: { isDuplicating },
  } = useDuplicateExpenseReportPresenter({
    expenseId: expense?.id ?? 0,
    onSuccess: handleDuplicateSuccess,
  })

  const {
    commands: { openPdf: _openPdf },
  } = useGenerateExpensePdfPresenter({
    expenseId: expense?.id ?? 0,
  })

  const expenseContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.PROCURIFY,
      ContentTypeModels.EXPENSEREPORT
    )
  )
  const expenseContentTypeId = expenseContentType?.id ?? 0

  const disableComments =
    !!featureSwitches.disable_comments_on_request_and_approve

  const isRequester = expense ? expense.requester.id === currentUser?.id : false

  const showSpendTrackerButton =
    budgetsEnabled && !!expense?.permissions.canApprove && !isSpendTrackerOpen

  const showComments = !disableComments && !!expense

  const canApprove = !!expense?.permissions.canApprove
  const canDelete = !!expense?.permissions.canDelete
  const showEditButton =
    !!expense?.permissions.canEdit && !expense?.permissions.canRevise
  const showReviseButton = !!expense?.permissions.canRevise
  const isPending = !!expense?.isPending
  const isApproved = !!expense?.isApproved
  const hasLogs = !!expense?.hasLogs
  const hasNextApproverChoices = !!expense?.hasNextApproverChoices
  const isFinalApproval = !!expense?.isFinalApproval
  const nextApprover = expense?.nextApprover ?? null

  const showSendReminder = isRequester && isPending && nextApprover !== null
  const showHistory = hasLogs
  const showGeneratePdf = isApproved

  const selectedNextApproverOption =
    expense?.nextApproverChoices.find((o) => o.id === selectedNextApproverId) ??
    null

  const handleOpenSpendTracker = useCallback(() => {
    setIsSpendTrackerOpen(true)
  }, [])

  const handleCloseSpendTracker = useCallback(() => {
    setIsSpendTrackerOpen(false)
  }, [])

  const handleChangeActiveTab = useCallback((tab: string) => {
    setActiveTab(tab)
  }, [])

  // Guarded command wrappers — prevent mutations firing when expense is not yet loaded
  const approveExpense = async () => {
    if (!expense) return
    await _approve()
  }

  const confirmOverBudget = async () => {
    if (!expense) return
    await _confirmOverBudget()
  }

  const denyExpense = async () => {
    if (!expense) return
    await _deny()
  }

  const deleteExpenseReport = async () => {
    if (!expense) return
    await _delete()
  }

  const duplicateExpense = async () => {
    if (!expense) return
    await _duplicate()
  }

  const openPdf = () => {
    if (!expense) return
    _openPdf()
  }

  return {
    data: {
      expense,
      isLoading,
    },
    states: {
      showSpendTracker: isSpendTrackerOpen,
      selectedNextApproverId,
      isRequester,
      showSpendTrackerButton,
      showComments,
      expenseContentTypeId,
      userId: currentUser?.id ?? 0,
      isApproving,
      isOverBudgetConfirmOpen,
      isDenying,
      isDeleting,
      isDeleteConfirmOpen,
      isDuplicating,
      activeTab,
      canApprove,
      canDelete,
      showEditButton,
      showReviseButton,
      isPending,
      isApproved,
      hasLogs,
      hasNextApproverChoices,
      isFinalApproval,
      nextApprover,
      showSendReminder,
      showHistory,
      showGeneratePdf,
      selectedNextApproverOption,
    },
    commands: {
      approveExpense,
      confirmOverBudget,
      cancelOverBudget,
      denyExpense,
      deleteExpenseReport,
      openDeleteConfirm,
      closeDeleteConfirm,
      duplicateExpense,
      openPdf,
    },
    methods: {
      onSelectedNextApproverIdChange: setSelectedNextApproverId,
      onOpenSpendTracker: handleOpenSpendTracker,
      onCloseSpendTracker: handleCloseSpendTracker,
      changeActiveTab: handleChangeActiveTab,
    },
  }
}
